import { useState, useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useSerialNumbersQuery } from '../adminTools.queries';
import { useRecalculateMaintenanceCountersMutation } from '../adminTools.mutations';
import type { RecalculationResult } from '../types/adminTools.types';

export interface UseMaintenanceRecalculateReturn {
  // Dialog state
  dialogOpen: boolean;
  loading: boolean;
  recalculating: boolean;
  // Data
  droneSerials: string[];
  selectedDrone: string;
  result: RecalculationResult | null;
  error: string;
  showDetails: boolean;
  // Actions
  openDialog: () => void;
  closeDialog: () => void;
  setSelectedDrone: (serial: string) => void;
  recalculate: () => Promise<void>;
  toggleDetails: () => void;
}

export function useMaintenanceRecalculate(): UseMaintenanceRecalculateReturn {
  const { showError, showSuccess } = useErrorHandler();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);

  // Server data — fetched only while the dialog is open.
  const serialsQuery = useSerialNumbersQuery(dialogOpen);
  const droneSerials = serialsQuery.data ?? [];
  const loading = dialogOpen && serialsQuery.isLoading;
  // Suppress a stale error while a fresh fetch is in flight (e.g. on dialog re-open).
  const serialsError =
    serialsQuery.isError && !serialsQuery.isFetching ? 'Failed to load drone list' : '';

  const recalculateMutation = useRecalculateMaintenanceCountersMutation();

  // Data
  const [selectedDrone, setSelectedDroneState] = useState('');
  const [result, setResult] = useState<RecalculationResult | null>(null);
  const [recalcError, setRecalcError] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  const error = recalcError || serialsError;

  const openDialog = useCallback(() => {
    setDialogOpen(true);
    setSelectedDroneState('');
    setResult(null);
    setRecalcError('');
    setShowDetails(false);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedDroneState('');
    setResult(null);
    setRecalcError('');
    setShowDetails(false);
  }, []);

  const setSelectedDrone = useCallback((serial: string) => {
    setSelectedDroneState(serial);
    setResult(null);
    setRecalcError('');
    setShowDetails(false);
  }, []);

  const recalculate = useCallback(async () => {
    if (!selectedDrone) return;

    setResult(null);
    setRecalcError('');

    try {
      const response = await recalculateMutation.mutateAsync(selectedDrone);
      setResult(response);
      setShowDetails(false);
      showSuccess(
        `Successfully recalculated ${selectedDrone}: ${response.summary.counters_reset} counters reset, ${response.summary.counters_unchanged} unchanged.`
      );
    } catch (err) {
      showError(err, 'Failed to recalculate maintenance counters');
      setRecalcError('Failed to recalculate maintenance counters');
    }
  }, [selectedDrone, recalculateMutation, showError, showSuccess]);

  const toggleDetails = useCallback(() => {
    setShowDetails((prev) => !prev);
  }, []);

  return {
    dialogOpen,
    loading,
    recalculating: recalculateMutation.isPending,
    droneSerials,
    selectedDrone,
    result,
    error,
    showDetails,
    openDialog,
    closeDialog,
    setSelectedDrone,
    recalculate,
    toggleDetails,
  };
}
